import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import {
  EVENT_TYPES, TRILOGY_PIPELINE, RELATIONSHIP_STATUSES, COUNTRIES,
  TONE, optOf, labelOf,
} from "@/config/trilogyOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dumbbell, Plus, Search, Building2, Users, CalendarCheck, CheckCircle2,
  UserPlus, CalendarClock, FolderHeart, DollarSign, Pencil, Phone, Mail,
} from "lucide-react";
import { toast } from "sonner";

const Pill = ({ arr, value, testid }) => {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;
  const o = optOf(arr, value); const t = TONE[o?.tone || "gray"];
  return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap"
    style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid={testid}>{o?.label || labelOf(arr, value)}</span>;
};

const MetricCard = ({ icon: Icon, label, value, accent, testid }) => (
  <Card className="p-3.5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center gap-2.5">
      <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1A` }}>
        <Icon size={17} style={{ color: accent }} />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-bold tabular-nums text-[#252221] leading-none">{value ?? 0}</div>
        <div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div>
      </div>
    </div>
  </Card>
);

const emptyForm = {
  facility_name: "", contact_name: "", phone: "", email: "", location: "", city: "", state_province: "",
  country: "", event_type: "", fitness_class_date: "", attendees: "", flyer_qr: "", leads_captured: "",
  consultations_booked: "", cases_created: "", follow_up_needed: false, pipeline_status: "facility_lead",
  relationship_status: "new", branch_id: "", notes: "",
};

export default function TrilogyOutreach() {
  const { user } = useAuth();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("trilogy_outreach");

  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", pipeline_status: "all", event_type: "all", relationship_status: "all", branch_id: "all" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const loadMetrics = useCallback(() => { api.get("/trilogy/metrics").then((r) => setMetrics(r.data || {})).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      ["pipeline_status", "event_type", "relationship_status", "branch_id"].forEach((k) => { if (filters[k] && filters[k] !== "all") params[k] = filters[k]; });
      const { data } = await api.get("/trilogy/outreach", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {}); loadMetrics(); }, [loadMetrics]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const refresh = () => { load(); loadMetrics(); };

  const openNew = () => { setForm({ ...emptyForm, branch_id: branches.length === 1 ? branches[0].id : "" }); setEditing(null); setOpen(true); };
  const openEdit = (r) => {
    const f = { ...emptyForm, ...r };
    ["attendees", "leads_captured", "consultations_booked", "cases_created"].forEach((k) => { f[k] = r[k] != null ? String(r[k]) : ""; });
    setForm(f); setEditing(r.id); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.facility_name) { toast.error("Facility / community name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      ["attendees", "leads_captured", "consultations_booked", "cases_created"].forEach((k) => { payload[k] = payload[k] === "" ? undefined : Number(payload[k]); });
      Object.keys(payload).forEach((k) => (payload[k] === "" || payload[k] === undefined) && delete payload[k]);
      payload.follow_up_needed = !!form.follow_up_needed;
      if (editing) await api.put(`/trilogy/outreach/${editing}`, payload);
      else await api.post("/trilogy/outreach", payload);
      toast.success(editing ? "Outreach updated" : "Outreach added");
      setOpen(false); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };

  const updatePipeline = async (rid, v) => {
    try { await api.put(`/trilogy/outreach/${rid}`, { pipeline_status: v }); toast.success("Pipeline updated"); refresh(); } catch { toast.error("Could not update"); }
  };
  const createLead = async (r) => {
    try {
      const { data } = await api.post(`/trilogy/outreach/${r.id}/create-lead`, {});
      toast.success(`Lead created for ${[data.lead.first_name, data.lead.last_name].filter(Boolean).join(" ") || "contact"}`);
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create lead"); }
  };

  const FilterSelect = ({ value, onChange, options, testid, all }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-full sm:w-[150px]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-[300px]"><SelectItem value="all">{all}</SelectItem>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><Dumbbell size={22} className="text-[#1C4358]" /> Trilogy Outreach</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Senior fitness events that softly introduce MovingMyMama as the trusted transition resource.</p>
        </div>
        {canWrite && <Button onClick={openNew} data-testid="new-outreach-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={17} className="mr-1.5" /> New Outreach</Button>}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-5" data-testid="trilogy-metrics">
        <MetricCard icon={Building2} label="Facilities contacted" value={metrics.facilities_contacted} accent="#1C4358" testid="metric-facilities" />
        <MetricCard icon={CalendarCheck} label="Events booked" value={metrics.events_booked} accent="#1C4358" testid="metric-events-booked" />
        <MetricCard icon={CheckCircle2} label="Events completed" value={metrics.events_completed} accent="#256B4B" testid="metric-events-completed" />
        <MetricCard icon={UserPlus} label="Leads captured" value={metrics.leads_captured} accent="#9A6516" testid="metric-leads" />
        <MetricCard icon={CalendarClock} label="Consultations booked" value={metrics.consultations_booked} accent="#9A6516" testid="metric-consultations" />
        <MetricCard icon={FolderHeart} label="Cases created" value={metrics.cases_created} accent="#9C1E22" testid="metric-cases" />
        <MetricCard icon={DollarSign} label="Revenue opportunities" value={metrics.revenue_opportunities} accent="#256B4B" testid="metric-revenue" />
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Search facility, contact, city..." className="pl-9 h-9" data-testid="outreach-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:flex gap-2">
            <FilterSelect value={filters.pipeline_status} onChange={(v) => setFilters((p) => ({ ...p, pipeline_status: v }))} options={TRILOGY_PIPELINE} testid="filter-pipeline" all="All stages" />
            <FilterSelect value={filters.event_type} onChange={(v) => setFilters((p) => ({ ...p, event_type: v }))} options={EVENT_TYPES} testid="filter-event-type" all="All event types" />
            <FilterSelect value={filters.relationship_status} onChange={(v) => setFilters((p) => ({ ...p, relationship_status: v }))} options={RELATIONSHIP_STATUSES} testid="filter-relationship" all="Any relationship" />
            <FilterSelect value={filters.branch_id} onChange={(v) => setFilters((p) => ({ ...p, branch_id: v }))} options={branches.map((b) => ({ value: b.id, label: b.name }))} testid="filter-branch" all="All branches" />
          </div>
        </div>
      </Card>

      {/* Table */}
      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><Dumbbell size={26} className="text-[#1C4358]" /></div>
            <p className="font-medium text-[#252221]">No outreach yet</p>
            <p className="text-sm text-muted-foreground">Add a facility or community to plan your first Trilogy fitness event.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="outreach-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Facility / community</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Event</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80 text-center">Leads / Consults / Cases</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Pipeline</TableHead>
                  <TableHead className="text-right text-xs font-semibold text-[#1C4358]/80">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="outreach-row">
                    <TableCell className="py-3">
                      <div className="font-semibold text-[#252221]">{r.facility_name || r.organization || "Facility"}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                        {r.contact_name && <span>{r.contact_name}</span>}
                        {[r.city, r.state_province].filter(Boolean).join(", ") && <span>· {[r.city, r.state_province].filter(Boolean).join(", ")}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.event_type ? labelOf(EVENT_TYPES, r.event_type) : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.fitness_class_date ? formatDate(r.fitness_class_date) : "—"}</TableCell>
                    <TableCell className="text-center text-sm tabular-nums">
                      <span className="text-[#9A6516] font-semibold">{r.leads_captured || 0}</span>
                      <span className="text-muted-foreground"> / </span>
                      <span className="text-[#9A6516] font-semibold">{r.consultations_booked || 0}</span>
                      <span className="text-muted-foreground"> / </span>
                      <span className="text-[#9C1E22] font-semibold">{r.cases_created || 0}</span>
                    </TableCell>
                    <TableCell>
                      {canWrite ? (
                        <Select value={r.pipeline_status || ""} onValueChange={(v) => updatePipeline(r.id, v)}>
                          <SelectTrigger className="h-8 w-[200px]" data-testid="outreach-pipeline-select"><SelectValue placeholder="Set stage" /></SelectTrigger>
                          <SelectContent className="max-h-[320px]">{TRILOGY_PIPELINE.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <Pill arr={TRILOGY_PIPELINE} value={r.pipeline_status} />}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canWrite && (
                        <>
                          <Button size="sm" variant="outline" className="mr-2" onClick={() => createLead(r)} data-testid="create-lead-button"><UserPlus size={14} className="mr-1" /> Create lead</Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid="edit-outreach-button"><Pencil size={14} /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">{items.length} outreach record{items.length === 1 ? "" : "s"}</p>

      {/* New / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto mmm-scroll" data-testid="outreach-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><Dumbbell size={20} className="text-[#1C4358]" /> {editing ? "Edit outreach" : "New Trilogy outreach"}</DialogTitle>
            <DialogDescription>Plan and track a senior fitness event at a facility or community.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Facility / community name *</Label><Input value={form.facility_name} onChange={(e) => setForm((p) => ({ ...p, facility_name: e.target.value }))} data-testid="outreach-facility-name" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Contact person</Label><Input value={form.contact_name} onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))} data-testid="outreach-contact-name" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Phone</Label><Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Location / venue</Label><Input value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">City</Label><Input value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">State / province</Label><Input value={form.state_province} onChange={(e) => setForm((p) => ({ ...p, state_province: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Country</Label>
                <Select value={form.country} onValueChange={(v) => setForm((p) => ({ ...p, country: v }))}>
                  <SelectTrigger data-testid="outreach-country"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Event type</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm((p) => ({ ...p, event_type: v }))}>
                  <SelectTrigger data-testid="outreach-event-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{EVENT_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Fitness class date</Label><Input type="date" value={form.fitness_class_date} onChange={(e) => setForm((p) => ({ ...p, fitness_class_date: e.target.value }))} data-testid="outreach-date" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Number of attendees</Label><Input type="number" value={form.attendees} onChange={(e) => setForm((p) => ({ ...p, attendees: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Flyer / QR code used</Label><Input value={form.flyer_qr} onChange={(e) => setForm((p) => ({ ...p, flyer_qr: e.target.value }))} placeholder="e.g. QR-Spring2026" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Pipeline stage</Label>
                <Select value={form.pipeline_status} onValueChange={(v) => setForm((p) => ({ ...p, pipeline_status: v }))}>
                  <SelectTrigger data-testid="outreach-pipeline-new"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">{TRILOGY_PIPELINE.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Relationship status</Label>
                <Select value={form.relationship_status} onValueChange={(v) => setForm((p) => ({ ...p, relationship_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{RELATIONSHIP_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Leads captured</Label><Input type="number" value={form.leads_captured} onChange={(e) => setForm((p) => ({ ...p, leads_captured: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Consultations booked</Label><Input type="number" value={form.consultations_booked} onChange={(e) => setForm((p) => ({ ...p, consultations_booked: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">MovingMyMama cases created</Label><Input type="number" value={form.cases_created} onChange={(e) => setForm((p) => ({ ...p, cases_created: e.target.value }))} /></div>
              {branches.length > 1 && (
                <div><Label className="mb-1.5 block text-[13px]">Branch</Label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm((p) => ({ ...p, branch_id: v }))}>
                    <SelectTrigger data-testid="outreach-branch"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2 flex items-center gap-3 rounded-lg border border-[#E6EDF2] px-3 py-2.5">
                <Switch checked={!!form.follow_up_needed} onCheckedChange={(v) => setForm((p) => ({ ...p, follow_up_needed: v }))} data-testid="outreach-followup" />
                <span className="text-sm text-[#252221]">Follow-up needed</span>
              </div>
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-outreach-button">{busy ? "Saving..." : editing ? "Save changes" : "Add outreach"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
